import { useState, useEffect, useRef } from 'react';
import { Card, Button, Input, Badge, Select, Toast } from '@atoms/index';
import { CuisineMultiSelect } from '@molecules/index';
import {
  ConfirmModal,
  StakeholderDocumentsModal,
  RestaurantApplicationFormModal,
} from '@organisms/index';
import { adminService, type AdminRestaurantCategory } from '@services/adminService';
import { geoService } from '@services/geoService';
import { geocodingService, type GeocodingResult } from '@services/geocodingService';
import { formatCurrency } from '@/utils/currency';
import {
  validateTurkishMobilePhone,
  validateTurkishPlate,
  normalizePlateInput,
  validateVkn,
  validateIban,
} from '@/utils/validation';
import { formatPhoneE164 } from '@/utils/format';
import { getCuisineFilterItems } from '@services/cuisineService';
import type { ServiceZone } from '@/types';
import type { SelectOption } from '@/components/atoms/Select';

type TabType = 'courier' | 'restaurant';

type StakeholderBadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';

const COURIER_TYPE_BADGE: Record<string, { label: string; variant: StakeholderBadgeVariant }> = {
  PLATFORM: { label: 'Platform Kuryesi', variant: 'info' },
  RESTAURANT: { label: 'Restoran Kuryesi', variant: 'success' },
};

const DELIVERY_MODEL_BADGE: Record<string, { label: string; variant: StakeholderBadgeVariant }> = {
  PLATFORM: { label: 'Platform Kuryesi', variant: 'info' },
  OWN_FLEET: { label: 'Restoran Kuryesi', variant: 'success' },
  HYBRID: { label: 'Hibrit', variant: 'warning' },
};

const getCourierTypeBadge = (courierType?: string | null) =>
  COURIER_TYPE_BADGE[courierType || 'PLATFORM'] ?? COURIER_TYPE_BADGE.PLATFORM;

const getDeliveryModelBadge = (deliveryProviderType?: string | null) =>
  DELIVERY_MODEL_BADGE[deliveryProviderType || 'PLATFORM'] ?? DELIVERY_MODEL_BADGE.PLATFORM;

interface CourierListItem {
  id: string;
  userId: string;
  user: {
    id: string;
    email: string | null;
    name: string | null;
    surname: string | null;
    phone: string | null;
  } | null;
  status: string;
  vehicleType: string | null;
  plateNumber: string | null;
  activeZoneId: string | null;
  currentLocationJson: { lat: number; lng: number } | null;
  rating: number;
  completedDeliveriesCount: number;
  activeOrdersCount: number;
  maxCapacity: number;
  cashBalanceLimit: string | number;
  baseDeliveryFee: number | null;
  distanceBonusPerKm: number | null;
  commissionRate: number | null;
  iban: string | null;
  courierType: string;
  restaurantId: string | null;
  restaurantName?: string | null;
  createdAt: string;
}

interface RestaurantListItem {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logoUrl: string | null;
  bannerUrl: string | null;
  phone: string | null;
  phoneSecondary: string | null;
  address: string | null;
  latitude?: number | null;
  longitude?: number | null;
  serviceZoneId: string;
  legalName: string | null;
  taxNumber: string | null;
  iban: string | null;
  commissionRate: string;
  billingCycle: string | null;
  customBillingCycleDays?: number | null;
  deliveryProviderType?: string;
  cuisineCategoryId?: string | null;
  cuisineCategoryIds?: string[];
  cuisineCategoryNames?: string[];
  isActive: boolean;
  isAcceptingOrders?: boolean;
  status: string;
  rating: number;
  reviewCount: number;
  manager: {
    id: string;
    email: string;
    name: string | null;
    surname: string | null;
    phone: string | null;
  } | null;
  createdAt: string;
}

export const StakeholderManagementTab = () => {
  const [activeTab, setActiveTab] = useState<TabType>('courier');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [couriers, setCouriers] = useState<CourierListItem[]>([]);
  const [restaurants, setRestaurants] = useState<RestaurantListItem[]>([]);
  const [isLoadingCouriers, setIsLoadingCouriers] = useState(false);
  const [isLoadingRestaurants, setIsLoadingRestaurants] = useState(false);
  const [courierPage, setCourierPage] = useState(1);
  const [restaurantPage, setRestaurantPage] = useState(1);
  const [courierLimit, setCourierLimit] = useState(20);
  const [restaurantLimit, setRestaurantLimit] = useState(20);
  const [courierTotalPages, setCourierTotalPages] = useState(1);
  const [courierTotal, setCourierTotal] = useState(0);
  const [restaurantTotalPages, setRestaurantTotalPages] = useState(1);
  const [restaurantTotal, setRestaurantTotal] = useState(0);
  const [courierSearch, setCourierSearch] = useState('');
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [courierSearchDebounced, setCourierSearchDebounced] = useState('');
  const [restaurantSearchDebounced, setRestaurantSearchDebounced] = useState('');
  const [editingCourier, setEditingCourier] = useState<CourierListItem | null>(null);
  const [creatingCourier, setCreatingCourier] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState<RestaurantListItem | null>(null);
  const [creatingRestaurant, setCreatingRestaurant] = useState(false);
  const [addingProductForRestaurant, setAddingProductForRestaurant] = useState<RestaurantListItem | null>(null);
  const [deleteRestaurantConfirm, setDeleteRestaurantConfirm] = useState<{ open: boolean; restaurant: RestaurantListItem | null }>({
    open: false,
    restaurant: null,
  });
  const [deleteCourierConfirm, setDeleteCourierConfirm] = useState<{ open: boolean; courier: CourierListItem | null }>({
    open: false,
    courier: null,
  });
  const [documentsModal, setDocumentsModal] = useState<{
    entityType: 'RESTAURANT' | 'COURIER';
    entityId: string;
    entityName: string;
  } | null>(null);
  const [applicationFormModal, setApplicationFormModal] = useState<{
    restaurantId: string;
    restaurantName: string;
  } | null>(null);
  const [toast, setToast] = useState<{ isVisible: boolean; message: string; type: 'success' | 'error' | 'info' }>({
    isVisible: false,
    message: '',
    type: 'info',
  });
  const [zones, setZones] = useState<ServiceZone[]>([]);
  const [isLoadingZones, setIsLoadingZones] = useState(false);

  useEffect(() => {
    loadZones();
    loadRestaurantsForDropdown();
  }, []);

  const loadRestaurantsForDropdown = async () => {
    try {
      const result = await adminService.getRestaurants(1, 200, '');
      setRestaurants(result.restaurants);
    } catch (err) {
      console.error('Failed to preload restaurants for dropdown:', err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setCourierSearchDebounced(courierSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [courierSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setRestaurantSearchDebounced(restaurantSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [restaurantSearch]);

  useEffect(() => {
    if (activeTab === 'courier') {
      setCourierPage(1);
      loadCouriers();
    } else {
      setRestaurantPage(1);
      loadRestaurants();
    }
  }, [activeTab, courierSearchDebounced, restaurantSearchDebounced]);

  useEffect(() => {
    if (activeTab === 'courier') {
      loadCouriers();
    } else {
      loadRestaurants();
    }
  }, [activeTab, courierPage, restaurantPage, courierLimit, restaurantLimit]);

  const loadZones = async () => {
    setIsLoadingZones(true);
    try {
      const response = await geoService.getServiceZones();
      setZones(response.zones || []);
    } catch (err) {
      console.error('Failed to load zones:', err);
      setErrorMessage('Zone bilgileri yüklenirken hata oluştu');
    } finally {
      setIsLoadingZones(false);
    }
  };

  const getZoneOptions = (): SelectOption[] => {
    return zones.map((zone) => ({
      value: zone.id,
      label: `${zone.city} - ${zone.district} - ${zone.name}`,
    }));
  };

  const getZoneLabel = (zoneId: string | null | undefined): string => {
    if (!zoneId) return '';
    const zone = zones.find((z) => z.id === zoneId);
    if (!zone) return zoneId;
    return `${zone.city} - ${zone.district} - ${zone.name}`;
  };

  const loadCouriers = async () => {
    setIsLoadingCouriers(true);
    try {
      const result = await adminService.getCouriers(courierPage, courierLimit, courierSearchDebounced);
      const safeCouriers = result.couriers.map((c: any) => {
        const user = c.user || null;
        return {
          ...c,
          user: user ? {
            id: user.id || null,
            email: user.email || null,
            name: user.name || null,
            surname: user.surname || null,
            phone: user.phone || null,
          } : null,
          cashBalanceLimit: c.cashBalanceLimit || '1000',
          courierType: c.courierType || 'PLATFORM',
          restaurantName: c.restaurantName ?? null,
        };
      });
      setCouriers(safeCouriers);
      setCourierTotalPages(result.pagination.totalPages);
      setCourierTotal(result.pagination.total || 0);
    } catch (err) {
      console.error('Failed to load couriers:', err);
    } finally {
      setIsLoadingCouriers(false);
    }
  };

  const loadRestaurants = async () => {
    setIsLoadingRestaurants(true);
    try {
      const result = await adminService.getRestaurants(restaurantPage, restaurantLimit, restaurantSearchDebounced);
      setRestaurants(result.restaurants);
      setRestaurantTotalPages(result.pagination.totalPages);
      setRestaurantTotal(result.pagination.total || 0);
    } catch (err) {
      console.error('Failed to load restaurants:', err);
    } finally {
      setIsLoadingRestaurants(false);
    }
  };

  const getApiErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.error?.message
    || (typeof error?.response?.data?.message === 'string' ? error.response.data.message : null)
    || fallback;

  const handleDeleteRestaurant = async () => {
    if (!deleteRestaurantConfirm.restaurant) return;
    try {
      await adminService.deleteRestaurant(deleteRestaurantConfirm.restaurant.id);
      setToast({ isVisible: true, message: 'Restoran başarıyla silindi.', type: 'success' });
      setEditingRestaurant(null);
      await loadRestaurants();
    } catch (error: any) {
      setToast({
        isVisible: true,
        message: getApiErrorMessage(error, 'Restoran silinemedi.'),
        type: 'error',
      });
    } finally {
      setDeleteRestaurantConfirm({ open: false, restaurant: null });
    }
  };

  const handleDeleteCourier = async () => {
    if (!deleteCourierConfirm.courier) return;
    try {
      await adminService.deleteCourier(deleteCourierConfirm.courier.id);
      setToast({ isVisible: true, message: 'Kurye başarıyla silindi.', type: 'success' });
      setEditingCourier(null);
      await loadCouriers();
    } catch (error: any) {
      setToast({
        isVisible: true,
        message: getApiErrorMessage(error, 'Kurye silinemedi.'),
        type: 'error',
      });
    } finally {
      setDeleteCourierConfirm({ open: false, courier: null });
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b border-gray-200 bg-white rounded-t-2xl">
        <button
          onClick={() => setActiveTab('courier')}
          className={`px-6 py-3 font-semibold transition-all duration-200 relative ${
            activeTab === 'courier'
              ? 'text-primary'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="relative z-10">🚴 Kuryeler</span>
          {activeTab === 'courier' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('restaurant')}
          className={`px-6 py-3 font-semibold transition-all duration-200 relative ${
            activeTab === 'restaurant'
              ? 'text-primary'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="relative z-10">🍽️ Restoranlar</span>
          {activeTab === 'restaurant' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
          )}
        </button>
      </div>

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {errorMessage}
        </div>
      )}

      {activeTab === 'courier' && (
        <>
          <Card className="shadow-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Kuryeler</h3>
                <p className="text-sm text-gray-500 mt-1">Kurye yönetimi ve takibi</p>
              </div>
              <button
                onClick={() => setCreatingCourier(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2"
                title="Yeni kurye ekle"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Yeni Kurye</span>
              </button>
            </div>

            <div className="mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex-1 w-full sm:max-w-md">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="🔍 İsim, email, telefon veya plaka ile ara..."
                    value={courierSearch}
                    onChange={(e) => setCourierSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {courierSearch && (
                    <button
                      onClick={() => setCourierSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm text-gray-600 whitespace-nowrap">Sayfa başına:</span>
                <Select
                  value={courierLimit.toString()}
                  onChange={(value) => {
                    setCourierLimit(Number(value));
                    setCourierPage(1);
                  }}
                  options={[
                    { value: '10', label: '10' },
                    { value: '20', label: '20' },
                    { value: '50', label: '50' },
                    { value: '100', label: '100' },
                  ]}
                  className="w-20"
                />
              </div>
            </div>
              {isLoadingCouriers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Yükleniyor...</p>
                </div>
              ) : couriers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz kurye bulunmuyor</p>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {couriers.map((courier) => (
                    <div
                      key={courier.id}
                      className="p-4 border border-gray-200 rounded-2xl hover:border-primary/30 hover:shadow-md transition-all duration-200 bg-white"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="font-bold text-lg text-gray-900 mb-1">
                            {courier.user?.name && courier.user?.surname
                              ? `${courier.user.name} ${courier.user.surname}`
                              : courier.user?.name
                              ? courier.user.name
                              : courier.user?.email
                              ? courier.user.email.split('@')[0]
                              : 'Kurye'}
                          </p>
                          {courier.user?.email && (
                            <p className="text-sm text-gray-600 mb-1">{courier.user.email}</p>
                          )}
                          {courier.user?.phone && (
                            <p className="text-xs text-gray-500">{courier.user.phone}</p>
                          )}
                          {courier.courierType === 'RESTAURANT' && courier.restaurantName && (
                            <p className="text-xs text-gray-500 mt-1">
                              Bağlı restoran: {courier.restaurantName}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-wrap justify-end">
                          {(() => {
                            const courierTypeBadge = getCourierTypeBadge(courier.courierType);
                            return (
                              <Badge variant={courierTypeBadge.variant} size="sm" title="Kurye tipi">
                                {courierTypeBadge.label}
                              </Badge>
                            );
                          })()}
                          <Badge
                            variant={
                              courier.status === 'ONLINE'
                                ? 'success'
                                : courier.status === 'BUSY'
                                ? 'warning'
                                : 'default'
                            }
                          >
                            {courier.status}
                          </Badge>
                          <button
                            onClick={() =>
                              setDocumentsModal({
                                entityType: 'COURIER',
                                entityId: courier.id,
                                entityName:
                                  courier.user?.name && courier.user?.surname
                                    ? `${courier.user.name} ${courier.user.surname}`
                                    : courier.user?.email || 'Kurye',
                              })
                            }
                            className="p-2 rounded-xl text-gray-500 hover:text-[#2A9D8F] hover:bg-[#2A9D8F]/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/30 focus:ring-offset-1"
                            title="Belgeler"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setEditingCourier(courier)}
                            className="p-2 rounded-xl text-gray-500 hover:text-primary hover:bg-primary/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1"
                            title="Düzenle"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteCourierConfirm({ open: true, courier })}
                            className="p-2 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-1"
                            title="Kuryeyi Sil"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                        <div>
                          <span className="font-medium">Araç:</span>{' '}
                          {courier.vehicleType || '—'}
                        </div>
                        <div>
                          <span className="font-medium">Plaka:</span>{' '}
                          {courier.plateNumber || '—'}
                        </div>
                        <div>
                          <span className="font-medium">Puan:</span> ⭐{' '}
                          {typeof courier.rating === 'number' ? courier.rating.toFixed(1) : parseFloat(String(courier.rating || 0)).toFixed(1)}
                        </div>
                        <div>
                          <span className="font-medium">Teslimat:</span>{' '}
                          {courier.completedDeliveriesCount}
                        </div>
                        <div>
                          <span className="font-medium">Kapasite:</span>{' '}
                          {courier.activeOrdersCount} / {courier.maxCapacity}
                        </div>
                        <div>
                          <span className="font-medium">Nakit Limit:</span>{' '}
                          {formatCurrency(Number(courier.cashBalanceLimit || 0))}
                        </div>
                      </div>
                      {(courier.baseDeliveryFee !== null || courier.distanceBonusPerKm !== null || courier.commissionRate !== null) && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Ödeme Ayarları:</p>
                          <div className="grid grid-cols-1 gap-1 text-xs text-gray-600">
                            {courier.baseDeliveryFee !== null && (
                              <div>
                                <span className="font-medium">Sipariş Başına:</span>{' '}
                                {formatCurrency(courier.baseDeliveryFee)}
                              </div>
                            )}
                            {courier.distanceBonusPerKm !== null && (
                              <div>
                                <span className="font-medium">Mesafe Bonusu:</span>{' '}
                                {formatCurrency(courier.distanceBonusPerKm)}/km
                              </div>
                            )}
                            {courier.commissionRate !== null && (
                              <div>
                                <span className="font-medium">Komisyon:</span>{' '}
                                {(courier.commissionRate * 100).toFixed(1)}%
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            {courierTotalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Toplam:</span>
                  <span className="font-semibold text-gray-900">
                    {courierTotal} kayıt
                  </span>
                  <span className="mx-2">•</span>
                  <span>
                    Sayfa <span className="font-semibold text-gray-900">{courierPage}</span> / {courierTotalPages}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCourierPage(1)}
                    disabled={courierPage === 1 || isLoadingCouriers}
                    className="px-2"
                    title="İlk sayfa"
                  >
                    ««
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCourierPage((p) => Math.max(1, p - 1))}
                    disabled={courierPage === 1 || isLoadingCouriers}
                    className="px-3"
                  >
                    ← Önceki
                  </Button>
                  <div className="flex items-center gap-1 mx-2">
                    {Array.from({ length: Math.min(5, courierTotalPages) }, (_, i) => {
                      let pageNum: number;
                      if (courierTotalPages <= 5) {
                        pageNum = i + 1;
                      } else if (courierPage <= 3) {
                        pageNum = i + 1;
                      } else if (courierPage >= courierTotalPages - 2) {
                        pageNum = courierTotalPages - 4 + i;
                      } else {
                        pageNum = courierPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCourierPage(pageNum)}
                          disabled={isLoadingCouriers}
                          className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-all ${
                            courierPage === pageNum
                              ? 'bg-primary text-white shadow-md'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setCourierPage((p) => Math.min(courierTotalPages, p + 1))
                    }
                    disabled={courierPage === courierTotalPages || isLoadingCouriers}
                    className="px-3"
                  >
                    Sonraki →
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCourierPage(courierTotalPages)}
                    disabled={courierPage === courierTotalPages || isLoadingCouriers}
                    className="px-2"
                    title="Son sayfa"
                  >
                    »»
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {activeTab === 'restaurant' && (
        <>
          <Card className="shadow-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Restoranlar</h3>
                <p className="text-sm text-gray-500 mt-1">Restoran yönetimi ve takibi</p>
              </div>
              <button
                onClick={() => setCreatingRestaurant(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2"
                title="Yeni restoran ekle"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Yeni Restoran</span>
              </button>
            </div>

            <div className="mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex-1 w-full sm:max-w-md">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="🔍 Restoran adı, slug veya telefon ile ara..."
                    value={restaurantSearch}
                    onChange={(e) => setRestaurantSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5"
                  />
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {restaurantSearch && (
                    <button
                      onClick={() => setRestaurantSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm text-gray-600 whitespace-nowrap">Sayfa başına:</span>
                <Select
                  value={restaurantLimit.toString()}
                  onChange={(value) => {
                    setRestaurantLimit(Number(value));
                    setRestaurantPage(1);
                  }}
                  options={[
                    { value: '10', label: '10' },
                    { value: '20', label: '20' },
                    { value: '50', label: '50' },
                    { value: '100', label: '100' },
                  ]}
                  className="w-20"
                />
              </div>
            </div>

            {isLoadingRestaurants ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Yükleniyor...</p>
                </div>
              ) : restaurants.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz restoran bulunmuyor</p>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {restaurants.map((restaurant) => (
                    <div
                      key={restaurant.id}
                      className="p-4 border border-gray-200 rounded-2xl hover:border-primary/30 hover:shadow-md transition-all duration-200 bg-white"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="font-bold text-lg text-gray-900 mb-1">{restaurant.name}</p>
                          <p className="text-sm text-gray-600 mb-2">{restaurant.slug}</p>
                          {restaurant.manager && (
                            <p className="text-xs text-gray-500">
                              👤 {restaurant.manager.name} {restaurant.manager.surname} ({restaurant.manager.email})
                            </p>
                          )}
                          {(restaurant.cuisineCategoryNames?.length ?? 0) > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {restaurant.cuisineCategoryNames!.map((cuisineName) => (
                                <Badge key={cuisineName} variant="secondary" size="sm">
                                  {cuisineName}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-wrap justify-end">
                          {(() => {
                            const deliveryModelBadge = getDeliveryModelBadge(restaurant.deliveryProviderType);
                            return (
                              <Badge variant={deliveryModelBadge.variant} size="sm" title="Kurye modeli">
                                {deliveryModelBadge.label}
                              </Badge>
                            );
                          })()}
                          {!restaurant.isActive && (
                            <Badge variant="danger">Silindi</Badge>
                          )}
                          <Badge
                            variant={
                              restaurant.status === 'ONLINE' ? 'success'
                              : restaurant.status === 'BUSY' ? 'warning'
                              : restaurant.status === 'SUSPENDED' ? 'error'
                              : 'default'
                            }
                          >
                            {restaurant.status === 'ONLINE' ? 'Çevrimiçi'
                              : restaurant.status === 'OFFLINE' ? 'Çevrimdışı'
                              : restaurant.status === 'BUSY' ? 'Meşgul'
                              : restaurant.status === 'SUSPENDED' ? 'Askıya Alındı'
                              : restaurant.status}
                          </Badge>
                          {restaurant.isActive && (
                            <button
                              onClick={() => setAddingProductForRestaurant(restaurant)}
                              className="p-2 rounded-xl text-gray-500 hover:text-[#2A9D8F] hover:bg-[#2A9D8F]/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/30 focus:ring-offset-1"
                              title="Ürün Ekle"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() =>
                              setDocumentsModal({
                                entityType: 'RESTAURANT',
                                entityId: restaurant.id,
                                entityName: restaurant.name,
                              })
                            }
                            className="p-2 rounded-xl text-gray-500 hover:text-[#2A9D8F] hover:bg-[#2A9D8F]/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/30 focus:ring-offset-1"
                            title="Belgeler"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() =>
                              setApplicationFormModal({
                                restaurantId: restaurant.id,
                                restaurantName: restaurant.name,
                              })
                            }
                            className="p-2 rounded-xl text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:ring-offset-1"
                            title="Başvuru Formu"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setEditingRestaurant(restaurant)}
                            className="p-2 rounded-xl text-gray-500 hover:text-primary hover:bg-primary/10 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1"
                            title="Düzenle"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {restaurant.isActive && (
                            <button
                              onClick={() => setDeleteRestaurantConfirm({ open: true, restaurant })}
                              className="p-2 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-1"
                              title="Restoranı Sil"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                        <div>
                          <span className="font-medium">Komisyon:</span>{' '}
                          {(parseFloat(restaurant.commissionRate) * 100).toFixed(1)}%
                        </div>
                        <div>
                          <span className="font-medium">Ödeme Döngüsü:</span>{' '}
                          {restaurant.billingCycle
                            ? restaurant.billingCycle === 'WEEKLY'
                              ? 'Haftalık'
                              : restaurant.billingCycle === 'MONTHLY'
                                ? 'Aylık'
                                : restaurant.billingCycle === 'CUSTOM'
                                  ? `Özel (${restaurant.customBillingCycleDays ?? '–'} gün)`
                                  : restaurant.billingCycle === 'AUTOMATIC'
                                    ? 'Otomatik'
                                    : restaurant.billingCycle
                            : 'Otomatik'}
                        </div>
                        <div>
                          <span className="font-medium">Puan:</span> ⭐{' '}
                          {typeof restaurant.rating === 'number' ? restaurant.rating.toFixed(1) : parseFloat(String(restaurant.rating || 0)).toFixed(1)}
                        </div>
                        <div>
                          <span className="font-medium">Yorum:</span>{' '}
                          {restaurant.reviewCount}
                        </div>
                      </div>
                      {restaurant.legalName && (
                        <div className="text-xs text-gray-500 mt-2">
                          <span className="font-medium">Yasal İsim:</span> {restaurant.legalName}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            {restaurantTotalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Toplam:</span>
                  <span className="font-semibold text-gray-900">
                    {restaurantTotal} kayıt
                  </span>
                  <span className="mx-2">•</span>
                  <span>
                    Sayfa <span className="font-semibold text-gray-900">{restaurantPage}</span> / {restaurantTotalPages}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setRestaurantPage(1)}
                    disabled={restaurantPage === 1 || isLoadingRestaurants}
                    className="px-2"
                    title="İlk sayfa"
                  >
                    ««
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setRestaurantPage((p) => Math.max(1, p - 1))}
                    disabled={restaurantPage === 1 || isLoadingRestaurants}
                    className="px-3"
                  >
                    ← Önceki
                  </Button>
                  <div className="flex items-center gap-1 mx-2">
                    {Array.from({ length: Math.min(5, restaurantTotalPages) }, (_, i) => {
                      let pageNum: number;
                      if (restaurantTotalPages <= 5) {
                        pageNum = i + 1;
                      } else if (restaurantPage <= 3) {
                        pageNum = i + 1;
                      } else if (restaurantPage >= restaurantTotalPages - 2) {
                        pageNum = restaurantTotalPages - 4 + i;
                      } else {
                        pageNum = restaurantPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setRestaurantPage(pageNum)}
                          disabled={isLoadingRestaurants}
                          className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-all ${
                            restaurantPage === pageNum
                              ? 'bg-primary text-white shadow-md'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setRestaurantPage((p) => Math.min(restaurantTotalPages, p + 1))
                    }
                    disabled={restaurantPage === restaurantTotalPages || isLoadingRestaurants}
                    className="px-3"
                  >
                    Sonraki →
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setRestaurantPage(restaurantTotalPages)}
                    disabled={restaurantPage === restaurantTotalPages || isLoadingRestaurants}
                    className="px-2"
                    title="Son sayfa"
                  >
                    »»
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {editingCourier && (
        <CourierEditModal
          courier={editingCourier}
          mode="edit"
          onClose={() => {
            setEditingCourier(null);
            loadCouriers();
          }}
          onDelete={() => setDeleteCourierConfirm({ open: true, courier: editingCourier })}
          zones={zones}
          isLoadingZones={isLoadingZones}
          getZoneOptions={getZoneOptions}
          getZoneLabel={getZoneLabel}
          restaurants={restaurants}
        />
      )}

      {creatingCourier && (
        <CourierEditModal
          courier={null}
          mode="create"
          onClose={() => {
            setCreatingCourier(false);
            loadCouriers();
          }}
          zones={zones}
          isLoadingZones={isLoadingZones}
          getZoneOptions={getZoneOptions}
          getZoneLabel={getZoneLabel}
          restaurants={restaurants}
        />
      )}

      {editingRestaurant && (
        <RestaurantEditModal
          restaurant={editingRestaurant}
          mode="edit"
          onClose={() => {
            setEditingRestaurant(null);
            loadRestaurants();
          }}
          onDelete={() => setDeleteRestaurantConfirm({ open: true, restaurant: editingRestaurant })}
          zones={zones}
          isLoadingZones={isLoadingZones}
          getZoneOptions={getZoneOptions}
          getZoneLabel={getZoneLabel}
        />
      )}

      {creatingRestaurant && (
        <RestaurantEditModal
          restaurant={null}
          mode="create"
          onClose={() => {
            setCreatingRestaurant(false);
            loadRestaurants();
          }}
          zones={zones}
          isLoadingZones={isLoadingZones}
          getZoneOptions={getZoneOptions}
          getZoneLabel={getZoneLabel}
        />
      )}

      {addingProductForRestaurant && (
        <AdminProductFormModal
          restaurant={addingProductForRestaurant}
          onClose={() => setAddingProductForRestaurant(null)}
          onSuccess={(message) => {
            setAddingProductForRestaurant(null);
            setToast({ isVisible: true, message, type: 'success' });
          }}
        />
      )}

      {documentsModal && (
        <StakeholderDocumentsModal
          isOpen
          entityType={documentsModal.entityType}
          entityId={documentsModal.entityId}
          entityName={documentsModal.entityName}
          onClose={() => setDocumentsModal(null)}
        />
      )}

      {applicationFormModal && (
        <RestaurantApplicationFormModal
          isOpen
          restaurantId={applicationFormModal.restaurantId}
          restaurantName={applicationFormModal.restaurantName}
          onClose={() => setApplicationFormModal(null)}
        />
      )}

      <ConfirmModal
        isOpen={deleteRestaurantConfirm.open}
        onClose={() => setDeleteRestaurantConfirm({ open: false, restaurant: null })}
        onConfirm={handleDeleteRestaurant}
        title="Restoranı Sil"
        description={
          deleteRestaurantConfirm.restaurant
            ? `"${deleteRestaurantConfirm.restaurant.name}" restoranını silmek istediğinize emin misiniz? Bu işlem geri alınamaz ve restoran artık listede görünmez.`
            : ''
        }
        confirmText="Evet, Sil"
        cancelText="İptal"
        variant="danger"
      />

      <ConfirmModal
        isOpen={deleteCourierConfirm.open}
        onClose={() => setDeleteCourierConfirm({ open: false, courier: null })}
        onConfirm={handleDeleteCourier}
        title="Kuryeyi Sil"
        description={
          deleteCourierConfirm.courier
            ? `"${deleteCourierConfirm.courier.user?.name || deleteCourierConfirm.courier.user?.email || 'Kurye'}" kaydını silmek istediğinize emin misiniz?${
                deleteCourierConfirm.courier.activeOrdersCount > 0
                  ? ' Bu kuryenin aktif teslimatı bulunuyor; silme işlemi başarısız olabilir.'
                  : ''
              }`
            : ''
        }
        confirmText="Evet, Sil"
        cancelText="İptal"
        variant="danger"
      />

      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((prev) => ({ ...prev, isVisible: false }))}
      />
    </div>
  );
};

interface CourierEditModalProps {
  courier: CourierListItem | null;
  mode: 'create' | 'edit';
  onClose: () => void;
  onDelete?: () => void;
  zones: ServiceZone[];
  isLoadingZones: boolean;
  getZoneOptions: () => SelectOption[];
  getZoneLabel: (zoneId: string | null | undefined) => string;
  restaurants: RestaurantListItem[];
}

function CourierEditModal({ courier, mode, onClose, onDelete, zones, isLoadingZones, getZoneOptions, getZoneLabel, restaurants }: CourierEditModalProps) {
  const [form, setForm] = useState({
    email: mode === 'create' ? '' : (courier?.user?.email || ''),
    name: mode === 'create' ? '' : (courier?.user?.name || ''),
    surname: mode === 'create' ? '' : (courier?.user?.surname || ''),
    phone: mode === 'create' ? '' : (courier?.user?.phone || ''),
    status: mode === 'create' ? 'OFFLINE' : (courier?.status || 'OFFLINE'),
    vehicleType: mode === 'create' ? '' : (courier?.vehicleType || ''),
    plateNumber: mode === 'create' ? '' : (courier?.plateNumber || ''),
    activeZoneId: mode === 'create' ? '' : (courier?.activeZoneId || ''),
    baseDeliveryFee: mode === 'create' ? undefined : (courier?.baseDeliveryFee ?? undefined),
    distanceBonusPerKm: mode === 'create' ? undefined : (courier?.distanceBonusPerKm ?? undefined),
    commissionRate: mode === 'create' ? undefined : (courier?.commissionRate ?? undefined),
    cashBalanceLimit: mode === 'create' ? 1000 : (courier?.cashBalanceLimit ? Number(courier.cashBalanceLimit) : 1000),
    maxCapacity: mode === 'create' ? 3 : (courier?.maxCapacity ?? 3),
    iban: mode === 'create' ? '' : (courier?.iban || ''),
    courierType: mode === 'create' ? 'PLATFORM' : (courier?.courierType || 'PLATFORM'),
    restaurantId: mode === 'create' ? '' : (courier?.restaurantId || ''),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [baseDeliveryFeeDisplay, setBaseDeliveryFeeDisplay] = useState('');
  const [distanceBonusDisplay, setDistanceBonusDisplay] = useState('');
  const [commissionRateDisplay, setCommissionRateDisplay] = useState('');
  const [cashBalanceLimitDisplay, setCashBalanceLimitDisplay] = useState('');
  const [maxCapacityDisplay, setMaxCapacityDisplay] = useState('');

  useEffect(() => {
    if (mode === 'create') {
      setBaseDeliveryFeeDisplay('');
      setDistanceBonusDisplay('');
      setCommissionRateDisplay('');
      setCashBalanceLimitDisplay('1000');
      setMaxCapacityDisplay('3');
    } else if (courier) {
      setBaseDeliveryFeeDisplay(courier.baseDeliveryFee != null ? String(courier.baseDeliveryFee) : '');
      setDistanceBonusDisplay(courier.distanceBonusPerKm != null ? String(courier.distanceBonusPerKm) : '');
      const rate = courier.commissionRate != null ? Number(courier.commissionRate) * 100 : '';
      setCommissionRateDisplay(rate !== '' ? String(rate) : '');
      setCashBalanceLimitDisplay(courier.cashBalanceLimit != null ? String(courier.cashBalanceLimit) : '1000');
      setMaxCapacityDisplay(courier.maxCapacity != null ? String(courier.maxCapacity) : '3');
    }
  }, [mode, courier?.id, courier?.baseDeliveryFee, courier?.distanceBonusPerKm, courier?.commissionRate, courier?.cashBalanceLimit, courier?.maxCapacity]);

  const parseNum = (s: string) => (s.trim() ? parseFloat(s.replace(',', '.')) : undefined);
  const parsePct = (s: string) => (s.trim() ? parseFloat(s.replace(',', '.')) / 100 : undefined);
  const parseIntSafe = (s: string) => (s.trim() ? parseInt(s.replace(/\D/g, ''), 10) : undefined);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    const phoneResult = validateTurkishMobilePhone(form.phone);
    const plateResult = validateTurkishPlate(form.plateNumber);
    const errors: Record<string, string> = {};
    if (!phoneResult.valid) errors.phone = phoneResult.message ?? '';
    if (!plateResult.valid) errors.plateNumber = plateResult.message ?? '';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const baseDeliveryFee = parseNum(baseDeliveryFeeDisplay);
    const distanceBonusPerKm = parseNum(distanceBonusDisplay);
    const commissionRate = parsePct(commissionRateDisplay);
    const cashBalanceLimit = parseNum(cashBalanceLimitDisplay) ?? form.cashBalanceLimit;
    const maxCapacity = parseIntSafe(maxCapacityDisplay) ?? form.maxCapacity;

    setIsSubmitting(true);
    try {
      const ibanNormalized = form.iban?.replace(/\s/g, '').toUpperCase() || undefined;
      const courierTypeValue = form.courierType as 'PLATFORM' | 'RESTAURANT' | undefined;
      const restaurantIdValue = form.courierType === 'RESTAURANT' && form.restaurantId ? form.restaurantId : undefined;

      if (mode === 'create') {
        await adminService.createCourier({
          email: form.email,
          name: form.name,
          surname: form.surname,
          phone: form.phone ? formatPhoneE164(form.phone) : undefined,
          vehicleType: form.vehicleType && ['MOTO', 'CAR', 'BIKE'].includes(form.vehicleType) ? (form.vehicleType as 'MOTO' | 'CAR' | 'BIKE') : undefined,
          plateNumber: form.plateNumber ? form.plateNumber.replace(/\s/g, '').toUpperCase() : undefined,
          activeZoneId: form.activeZoneId || undefined,
          baseDeliveryFee,
          distanceBonusPerKm,
          commissionRate,
          cashBalanceLimit,
          maxCapacity: maxCapacity ?? 3,
          iban: ibanNormalized,
          courierType: courierTypeValue,
          restaurantId: restaurantIdValue,
        });
      } else {
        if (!courier) return;
        await adminService.updateCourier(courier.id, {
          name: form.name,
          surname: form.surname,
          phone: form.phone ? formatPhoneE164(form.phone) : form.phone,
          vehicleType: form.vehicleType && ['MOTO', 'CAR', 'BIKE'].includes(form.vehicleType) ? (form.vehicleType as 'MOTO' | 'CAR' | 'BIKE') : undefined,
          plateNumber: form.plateNumber ? form.plateNumber.replace(/\s/g, '').toUpperCase() : form.plateNumber,
          activeZoneId: form.activeZoneId,
          baseDeliveryFee,
          distanceBonusPerKm,
          commissionRate,
          cashBalanceLimit,
          maxCapacity,
          iban: ibanNormalized,
          courierType: courierTypeValue,
          restaurantId: restaurantIdValue,
        });
        if (form.status !== courier.status) {
          await adminService.updateCourierStatus(courier.id, form.status as any);
        }
      }
      onClose();
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error?.message || 'İşlem başarısız');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between shrink-0 px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {mode === 'create' ? 'Yeni Kurye Oluştur' : 'Kurye Düzenle'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {mode === 'create' ? 'Yeni bir kurye hesabı oluşturun' : 'Kurye bilgilerini güncelleyin'}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        {errorMessage && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1 overflow-hidden flex">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="max-w-2xl space-y-1">
              {mode === 'create' && (
                <>
                  <RestaurantFormSectionTitle>Hesap</RestaurantFormSectionTitle>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <RestaurantFormField label="E-posta" required>
                      <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full"
                      />
                    </RestaurantFormField>
                    <RestaurantFormField label="Ad" required>
                      <Input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full"
                      />
                    </RestaurantFormField>
                    <RestaurantFormField label="Soyad" required>
                      <Input
                        required
                        value={form.surname}
                        onChange={(e) => setForm({ ...form, surname: e.target.value })}
                        className="w-full"
                      />
                    </RestaurantFormField>
                  </div>
                </>
              )}

              <RestaurantFormSectionTitle>İletişim{mode === 'edit' ? ' & durum' : ''}</RestaurantFormSectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {mode === 'edit' && (
                  <>
                    <RestaurantFormField label="Ad">
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full" />
                    </RestaurantFormField>
                    <RestaurantFormField label="Soyad">
                      <Input value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} className="w-full" />
                    </RestaurantFormField>
                  </>
                )}
                <RestaurantFormField label="Telefon" error={fieldErrors.phone}>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    value={form.phone}
                    onChange={(e) => {
                      setForm({ ...form, phone: e.target.value });
                      if (fieldErrors.phone) {
                        const r = validateTurkishMobilePhone(e.target.value.trim());
                        setFieldErrors((prev) => ({ ...prev, phone: r.valid ? '' : (r.message ?? '') }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      const r = validateTurkishMobilePhone(value);
                      setFieldErrors((prev) => ({ ...prev, phone: r.valid ? '' : (r.message ?? '') }));
                    }}
                    placeholder="+905551234567"
                    maxLength={14}
                    className={fieldErrors.phone ? 'border-red-300' : ''}
                  />
                </RestaurantFormField>
                {mode === 'edit' && (
                  <div>
                    <Select
                      label="Durum"
                      value={form.status}
                      onChange={(value) => setForm({ ...form, status: value as string })}
                      options={[
                        { value: 'OFFLINE', label: 'OFFLINE' },
                        { value: 'ONLINE', label: 'ONLINE' },
                        { value: 'BUSY', label: 'BUSY' },
                        { value: 'ON_DELIVERY', label: 'ON_DELIVERY' },
                        { value: 'PAUSED', label: 'PAUSED' },
                      ]}
                      size="md"
                    />
                  </div>
                )}
              </div>

              <RestaurantFormSectionTitle>Araç</RestaurantFormSectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Select
                    label="Araç tipi"
                    value={form.vehicleType}
                    onChange={(value) => setForm({ ...form, vehicleType: value as string })}
                    options={[
                      { value: '', label: 'Seçiniz' },
                      { value: 'MOTO', label: 'Moto' },
                      { value: 'CAR', label: 'Araba' },
                      { value: 'BIKE', label: 'Bisiklet' },
                    ]}
                    placeholder="Araç tipi"
                    size="md"
                  />
                </div>
                <RestaurantFormField label="Plaka" error={fieldErrors.plateNumber} hint="34 ABC 1234 veya 01A123">
                  <Input
                    value={form.plateNumber}
                    onChange={(e) => {
                      const normalized = normalizePlateInput(e.target.value);
                      setForm({ ...form, plateNumber: normalized });
                      if (fieldErrors.plateNumber) {
                        const r = validateTurkishPlate(normalized);
                        setFieldErrors((prev) => ({ ...prev, plateNumber: r.valid ? '' : (r.message ?? '') }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      const r = validateTurkishPlate(value);
                      setFieldErrors((prev) => ({ ...prev, plateNumber: r.valid ? '' : (r.message ?? '') }));
                    }}
                    placeholder="34 ABC 1234"
                    maxLength={11}
                    className={fieldErrors.plateNumber ? 'border-red-300' : ''}
                  />
                </RestaurantFormField>
              </div>

              <RestaurantFormSectionTitle>Bölge</RestaurantFormSectionTitle>
              <div>
                <Select
                  label="Aktif bölge"
                  options={getZoneOptions()}
                  value={form.activeZoneId || undefined}
                  onChange={(value) => setForm({ ...form, activeZoneId: (value as string) || '' })}
                  placeholder={isLoadingZones ? 'Yükleniyor...' : 'Bölge seçin (opsiyonel)'}
                  disabled={isLoadingZones}
                  searchable
                />
                {form.activeZoneId && (
                  <p className="mt-1 text-xs text-gray-500">Seçili: {getZoneLabel(form.activeZoneId)}</p>
                )}
              </div>

              <RestaurantFormSectionTitle>Ödeme ayarları</RestaurantFormSectionTitle>
              <p className="text-xs text-gray-500 mb-3">Boş bırakılırsa bölge bazlı ücret kullanılır.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RestaurantFormField label="Sipariş başı ücret (₺)" hint="Sabit ücret, boş = bölge bazlı">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={baseDeliveryFeeDisplay}
                    onChange={(e) => setBaseDeliveryFeeDisplay(e.target.value)}
                    className="w-full"
                    placeholder="Boş bırakılabilir"
                  />
                </RestaurantFormField>
                <RestaurantFormField label="Mesafe bonusu (₺/km)" hint="Örn: 2.50">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={distanceBonusDisplay}
                    onChange={(e) => setDistanceBonusDisplay(e.target.value)}
                    className="w-full"
                    placeholder="2.50"
                  />
                </RestaurantFormField>
                <RestaurantFormField label="Komisyon (%)" hint="Örn: 5 = %5">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={commissionRateDisplay}
                    onChange={(e) => setCommissionRateDisplay(e.target.value)}
                    className="w-full"
                    placeholder="5"
                  />
                </RestaurantFormField>
              </div>

              <RestaurantFormSectionTitle>Kurye tipi & IBAN</RestaurantFormSectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Select
                    label="Kurye tipi"
                    value={form.courierType}
                    onChange={(value) => setForm({ ...form, courierType: value as string, restaurantId: value === 'PLATFORM' ? '' : form.restaurantId })}
                    options={[
                      { value: 'PLATFORM', label: 'Platform kuryesi' },
                      { value: 'RESTAURANT', label: 'Restoran kuryesi' },
                    ]}
                    size="md"
                  />
                </div>
                {form.courierType === 'RESTAURANT' && (
                  <div>
                    <Select
                      label="Bağlı restoran"
                      value={form.restaurantId || undefined}
                      onChange={(value) => setForm({ ...form, restaurantId: value as string })}
                      options={restaurants.map((r) => ({ value: r.id, label: r.name }))}
                      placeholder="Restoran seçin"
                      searchable
                      size="md"
                    />
                  </div>
                )}
                <div className={form.courierType === 'RESTAURANT' ? '' : 'sm:col-span-2'}>
                  <RestaurantFormField label="IBAN" hint="TR + 24 rakam">
                    <Input
                      value={form.iban}
                      onChange={(e) => setForm({ ...form, iban: e.target.value })}
                      placeholder="TR330006100519786457841326"
                      className="w-full"
                    />
                  </RestaurantFormField>
                </div>
              </div>

              <RestaurantFormSectionTitle>Limitler</RestaurantFormSectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RestaurantFormField
                  label="Nakit bakiye limiti (₺)"
                  required={mode === 'create'}
                  hint="Varsayılan: 1000"
                >
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={cashBalanceLimitDisplay}
                    onChange={(e) => setCashBalanceLimitDisplay(e.target.value)}
                    className="w-full"
                    placeholder="1000"
                  />
                </RestaurantFormField>
                <RestaurantFormField
                  label="Maks. kapasite"
                  required={mode === 'create'}
                  hint="Aynı anda taşınabilecek sipariş sayısı. Varsayılan: 3"
                >
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={maxCapacityDisplay}
                    onChange={(e) => setMaxCapacityDisplay(e.target.value.replace(/\D/g, '').slice(0, 2))}
                    className="w-full"
                    placeholder="3"
                  />
                </RestaurantFormField>
              </div>
            </div>
          </div>

          <div className="shrink-0 flex gap-2 px-6 py-4 border-t border-gray-200 bg-[#F9FAFB] rounded-b-2xl">
            {mode === 'edit' && onDelete && (
              <Button type="button" variant="secondary" onClick={onDelete} className="text-red-600 border-red-200 hover:bg-red-50">
                Kuryeyi Sil
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'İşleniyor...' : mode === 'create' ? 'Kurye oluştur' : 'Kaydet'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              İptal
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

interface RestaurantEditModalProps {
  restaurant: RestaurantListItem | null;
  mode: 'create' | 'edit';
  onClose: () => void;
  onDelete?: () => void;
  zones: ServiceZone[];
  isLoadingZones: boolean;
  getZoneOptions: () => SelectOption[];
  getZoneLabel: (zoneId: string | null | undefined) => string;
}

function RestaurantFormSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-sm font-semibold text-gray-800 border-l-4 border-primary pl-3 mb-3 mt-6 first:mt-0">
      {children}
    </h4>
  );
}

function RestaurantFormField({
  label,
  required,
  error,
  children,
  hint,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

function RestaurantEditModal({ restaurant, mode, onClose, onDelete, zones, isLoadingZones, getZoneOptions, getZoneLabel }: RestaurantEditModalProps) {
  const initialManagerEmail = mode === 'create' ? '' : (restaurant?.manager?.email || '');
  const initialIsAcceptingOrders =
    mode === 'create' ? true : (restaurant?.isAcceptingOrders ?? restaurant?.status === 'ONLINE');
  const [form, setForm] = useState({
    email: mode === 'create' ? '' : (restaurant?.manager?.email || ''),
    name: mode === 'create' ? '' : (restaurant?.manager?.name || ''),
    surname: mode === 'create' ? '' : (restaurant?.manager?.surname || ''),
    phone: mode === 'create' ? '' : (restaurant?.phone || ''),
    phoneSecondary: mode === 'create' ? '' : (restaurant?.phoneSecondary ?? ''),
    restaurantName: mode === 'create' ? '' : (restaurant?.name || ''),
    slug: mode === 'create' ? '' : (restaurant?.slug || ''),
    description: mode === 'create' ? '' : (restaurant?.description || ''),
    serviceZoneId: mode === 'create' ? '' : (restaurant?.serviceZoneId || ''),
    legalName: mode === 'create' ? '' : (restaurant?.legalName || ''),
    taxNumber: mode === 'create' ? '' : (restaurant?.taxNumber || ''),
    iban: mode === 'create' ? '' : (restaurant?.iban || ''),
    commissionRate: mode === 'create' ? 0.1 : parseFloat(restaurant?.commissionRate || '0.1'),
    billingCycle: mode === 'create' ? undefined : (restaurant?.billingCycle || undefined),
    customBillingCycleDays:
      mode === 'create' ? undefined : (restaurant?.customBillingCycleDays ?? undefined),
    address: mode === 'create' ? '' : (restaurant?.address || ''),
    latitude: mode === 'create' ? null : (restaurant?.latitude != null ? Number(restaurant.latitude) : null),
    longitude: mode === 'create' ? null : (restaurant?.longitude != null ? Number(restaurant.longitude) : null),
    isAcceptingOrders: initialIsAcceptingOrders,
    deliveryProviderType: mode === 'create' ? 'PLATFORM' : (restaurant?.deliveryProviderType || 'PLATFORM'),
    cuisineCategoryIds:
      mode === 'create'
        ? []
        : (restaurant?.cuisineCategoryIds?.length
            ? restaurant.cuisineCategoryIds
            : restaurant?.cuisineCategoryId
              ? [restaurant.cuisineCategoryId]
              : []),
  });
  const [cuisineCategories, setCuisineCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [addressInputValue, setAddressInputValue] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<GeocodingResult[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const addressSuggestionsRef = useRef<HTMLDivElement>(null);
  const addressSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [commissionRateDisplay, setCommissionRateDisplay] = useState('');
  const [customBillingCycleDaysDisplay, setCustomBillingCycleDaysDisplay] = useState('');

  useEffect(() => {
    getCuisineFilterItems()
      .then((items) =>
        setCuisineCategories(
          items
            .filter((item) => item.id !== 'all')
            .map((item) => ({ id: item.id, name: item.name })),
        ),
      )
      .catch(() => setCuisineCategories([]));
  }, []);

  useEffect(() => {
    const rate = mode === 'create' ? 0.1 : parseFloat(restaurant?.commissionRate || '0.1');
    setCommissionRateDisplay(Number.isNaN(rate) ? '' : String(rate));
    const days = restaurant?.customBillingCycleDays ?? undefined;
    setCustomBillingCycleDaysDisplay(days != null ? String(days) : '');
  }, [mode, restaurant?.id, restaurant?.commissionRate, restaurant?.customBillingCycleDays]);

  useEffect(() => {
    if (mode === 'edit' && restaurant?.address?.trim()) {
      setAddressInputValue(restaurant.address.trim());
    } else {
      setAddressInputValue('');
    }
  }, [mode, restaurant?.id, restaurant?.address]);

  const runAddressSearch = (query: string) => {
    const q = query.trim();
    if (q.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    setIsSearchingAddress(true);
    setAddressSuggestions([]);
    geocodingService
      .searchAddresses(q)
      .then((results) => setAddressSuggestions(results))
      .finally(() => setIsSearchingAddress(false));
  };

  const scheduleAddressSearch = (value: string) => {
    if (addressSearchDebounceRef.current) {
      clearTimeout(addressSearchDebounceRef.current);
      addressSearchDebounceRef.current = null;
    }
    const q = value.trim();
    if (q.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    addressSearchDebounceRef.current = setTimeout(() => runAddressSearch(value), 300);
  };

  useEffect(() => () => {
    if (addressSearchDebounceRef.current) {
      clearTimeout(addressSearchDebounceRef.current);
      addressSearchDebounceRef.current = null;
    }
  }, []);

  const extractErrorMessage = (error: any): string => {
    if (error.response?.data) {
      const data = error.response.data;
      if (data.error?.message) {
        return data.error.message;
      }
      if (data.message) {
        if (Array.isArray(data.message)) {
          return data.message.join(', ');
        }
        return data.message;
      }
      if (data.error && typeof data.error === 'string') {
        return data.error;
      }
    }
    return error.message || 'İşlem sırasında bir hata oluştu';
  };

  const slugFromName = (name: string) =>
    name
      .trim()
      .toLowerCase()
      .replace(/[\s\u00C0-\u024F]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || '';

  const handleSelectAddress = (result: GeocodingResult) => {
    const selected = result.fullAddress || result.displayName;
    setForm((prev) => ({
      ...prev,
      address: selected,
      latitude: result.latitude,
      longitude: result.longitude,
    }));
    setAddressInputValue(selected);
    setAddressSuggestions([]);
  };

  const extractFieldErrors = (error: any): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (error.response?.data?.message) {
      const messages = Array.isArray(error.response.data.message)
        ? error.response.data.message
        : [error.response.data.message];
      messages.forEach((msg: string) => {
        const lowerMsg = msg.toLowerCase();
        if ((lowerMsg.includes('ikinci') || lowerMsg.includes('second')) && (lowerMsg.includes('phone') || lowerMsg.includes('telefon'))) {
          errors.phoneSecondary = msg.includes('Telefon') ? msg : 'İkinci telefon geçerli bir Türk telefon numarası formatında olmalıdır';
        } else if (lowerMsg.includes('phone') || lowerMsg.includes('telefon')) {
          errors.phone = msg.includes('Telefon') ? msg : 'Telefon numarası geçerli bir Türk telefon numarası formatında olmalıdır (örn: +905551234567)';
        } else if (lowerMsg.includes('name') && (lowerMsg.includes('restoran') || lowerMsg.includes('restaurant'))) {
          errors.name = msg;
        } else if (lowerMsg.includes('slug') || lowerMsg.includes('url adresi')) {
          errors.slug = msg.replace(/slug/gi, 'URL adresi');
        } else if (lowerMsg.includes('servicezone') || lowerMsg.includes('service zone')) {
          errors.serviceZoneId = msg;
        } else if (lowerMsg.includes('legalname') || lowerMsg.includes('yasal isim') || lowerMsg.includes('yasal name')) {
          errors.legalName = msg;
        } else if (lowerMsg.includes('taxnumber') || lowerMsg.includes('vergi numarası') || lowerMsg.includes('tax number')) {
          errors.taxNumber = msg;
        } else if (lowerMsg.includes('iban')) {
          errors.iban = msg;
        } else if (lowerMsg.includes('commission') || lowerMsg.includes('komisyon')) {
          errors.commissionRate = msg;
        } else if (lowerMsg.includes('billing') || lowerMsg.includes('ödeme döngüsü')) {
          errors.billingCycle = msg;
        } else if (lowerMsg.includes('custom') || lowerMsg.includes('özel') || lowerMsg.includes('gün')) {
          errors.customBillingCycleDays = msg;
        }
      });
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    const errs: Record<string, string> = {};
    const taxVal = form.taxNumber?.trim() ?? '';
    if (taxVal) {
      const rTax = validateVkn(taxVal);
      if (!rTax.valid) errs.taxNumber = rTax.message ?? '';
    }
    const rIban = validateIban(form.iban?.trim() ?? '', true);
    if (!rIban.valid) errs.iban = rIban.message ?? '';
    const phoneTrim = form.phone?.trim() ?? '';
    if (phoneTrim) {
      const rPhone = validateTurkishMobilePhone(phoneTrim);
      if (!rPhone.valid) errs.phone = rPhone.message ?? '';
    }
    const phone2Trim = form.phoneSecondary?.trim() ?? '';
    if (phone2Trim) {
      const rPhone2 = validateTurkishMobilePhone(phone2Trim);
      if (!rPhone2.valid) errs.phoneSecondary = rPhone2.message ?? '';
    }
    if (form.billingCycle === 'CUSTOM') {
      const daysNum = customBillingCycleDaysDisplay.trim()
        ? parseInt(customBillingCycleDaysDisplay.trim(), 10)
        : NaN;
      if (Number.isNaN(daysNum) || daysNum < 1 || daysNum > 365) {
        errs.customBillingCycleDays = 'Özel ödeme döngüsü için 1–365 arası gün sayısı giriniz.';
      }
    }
    const rateNum = commissionRateDisplay.trim()
      ? parseFloat(commissionRateDisplay.replace(',', '.'))
      : NaN;
    if (commissionRateDisplay.trim() && (Number.isNaN(rateNum) || rateNum < 0 || rateNum > 1)) {
      errs.commissionRate = 'Komisyon oranı 0–1 arası olmalıdır (örn: 0.10 veya 0,10).';
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    if (mode === 'edit' && form.email.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(form.email.trim())) {
        setFieldErrors({ email: 'Geçerli bir e-posta adresi giriniz' });
        return;
      }
    }
    setFieldErrors({});
    setIsSubmitting(true);

    const ibanNormalized = (form.iban ?? '').replace(/\s/g, '').toUpperCase() || undefined;
    const phoneE164 = phoneTrim ? formatPhoneE164(phoneTrim) : undefined;
    const phoneSecondaryE164 = phone2Trim ? formatPhoneE164(phone2Trim) : undefined;

    const parsedRate = commissionRateDisplay.trim()
      ? parseFloat(commissionRateDisplay.replace(',', '.'))
      : form.commissionRate;
    const commissionRate = !Number.isNaN(parsedRate) && parsedRate >= 0 && parsedRate <= 1
      ? parsedRate
      : form.commissionRate;
    const customDays =
      form.billingCycle === 'CUSTOM' && customBillingCycleDaysDisplay.trim()
        ? parseInt(customBillingCycleDaysDisplay.trim(), 10)
        : undefined;

    try {
      if (mode === 'create') {
        await adminService.createRestaurant({
          email: form.email,
          name: form.name,
          surname: form.surname,
          restaurantName: form.restaurantName,
          serviceZoneId: form.serviceZoneId,
          legalName: form.legalName || undefined,
          taxNumber: form.taxNumber?.trim() || undefined,
          iban: ibanNormalized,
          commissionRate,
          billingCycle: form.billingCycle,
          customBillingCycleDays: form.billingCycle === 'CUSTOM' ? customDays : undefined,
          phone: phoneE164,
          phoneSecondary: phoneSecondaryE164,
          address: form.address || undefined,
          latitude: form.latitude ?? undefined,
          longitude: form.longitude ?? undefined,
          deliveryProviderType: form.deliveryProviderType as 'PLATFORM' | 'OWN_FLEET' | 'HYBRID',
          cuisineCategoryIds: form.cuisineCategoryIds,
        });
      } else {
        if (!restaurant) return;
        const trimmedEmail = form.email.trim();
        if (
          restaurant.manager &&
          trimmedEmail &&
          trimmedEmail !== initialManagerEmail.trim()
        ) {
          await adminService.updateRestaurantManagerEmail(restaurant.id, trimmedEmail);
        }
        await adminService.updateRestaurant(restaurant.id, {
          name: form.restaurantName,
          description: form.description,
          phone: phoneE164,
          phoneSecondary: phoneSecondaryE164,
          address: form.address,
          serviceZoneId: form.serviceZoneId,
          legalName: form.legalName || undefined,
          taxNumber: form.taxNumber?.trim() || undefined,
          iban: ibanNormalized,
          commissionRate,
          billingCycle: form.billingCycle,
          customBillingCycleDays: form.billingCycle === 'CUSTOM' ? customDays ?? null : null,
          isAcceptingOrders: form.isAcceptingOrders,
          latitude: form.latitude ?? undefined,
          longitude: form.longitude ?? undefined,
          deliveryProviderType: form.deliveryProviderType as 'PLATFORM' | 'OWN_FLEET' | 'HYBRID',
          cuisineCategoryIds: form.cuisineCategoryIds,
        });
        if (form.isAcceptingOrders !== initialIsAcceptingOrders) {
          await adminService.updateRestaurantStatus(restaurant.id, form.isAcceptingOrders);
        }
      }
      onClose();
    } catch (error: any) {
      const fieldErrs = extractFieldErrors(error);
      if (Object.keys(fieldErrs).length > 0) {
        setFieldErrors(fieldErrs);
      } else {
        setErrorMessage(extractErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between shrink-0 px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {mode === 'create' ? 'Yeni Restoran Oluştur' : 'Restoran Düzenle'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {mode === 'create' ? 'Yeni bir restoran hesabı oluşturun' : 'Restoran bilgilerini güncelleyin'}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        {errorMessage && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1 overflow-hidden flex">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="max-w-2xl space-y-1">
              {mode === 'create' && (
                <>
                  <RestaurantFormSectionTitle>Hesap (Manager)</RestaurantFormSectionTitle>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <RestaurantFormField label="Manager e-posta" required error={fieldErrors.email}>
                      <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => {
                          setForm({ ...form, email: e.target.value });
                          if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: '' });
                        }}
                        className={fieldErrors.email ? 'border-red-300' : ''}
                      />
                    </RestaurantFormField>
                    <RestaurantFormField label="Manager ad" required error={fieldErrors.name}>
                      <Input
                        required
                        value={form.name}
                        onChange={(e) => {
                          setForm({ ...form, name: e.target.value });
                          if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: '' });
                        }}
                        className={fieldErrors.name ? 'border-red-300' : ''}
                      />
                    </RestaurantFormField>
                    <RestaurantFormField label="Manager soyad" required error={fieldErrors.surname}>
                      <Input
                        required
                        value={form.surname}
                        onChange={(e) => {
                          setForm({ ...form, surname: e.target.value });
                          if (fieldErrors.surname) setFieldErrors({ ...fieldErrors, surname: '' });
                        }}
                        className={fieldErrors.surname ? 'border-red-300' : ''}
                      />
                    </RestaurantFormField>
                  </div>
                </>
              )}

              {mode === 'edit' && restaurant?.isActive && (
                <>
                  <RestaurantFormSectionTitle>Manager E-posta</RestaurantFormSectionTitle>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <RestaurantFormField label="Manager e-posta" required error={fieldErrors.email}>
                      <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => {
                          setForm({ ...form, email: e.target.value });
                          if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: '' });
                        }}
                        className={fieldErrors.email ? 'border-red-300' : ''}
                      />
                    </RestaurantFormField>
                  </div>
                </>
              )}

              <RestaurantFormSectionTitle>Restoran</RestaurantFormSectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RestaurantFormField label="Restoran adı" required error={fieldErrors.name}>
                  <Input
                    required
                    value={form.restaurantName}
                    onChange={(e) => {
                      setForm({ ...form, restaurantName: e.target.value });
                      if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: '' });
                    }}
                    className={fieldErrors.name ? 'border-red-300' : ''}
                  />
                </RestaurantFormField>
                <RestaurantFormField
                  label="URL (slug)"
                  hint={
                    mode === 'create'
                      ? 'Restoran adına göre otomatik oluşturulur.'
                      : 'Web adresinde görünen URL.'
                  }
                >
                  <Input
                    readOnly
                    value={mode === 'create' ? slugFromName(form.restaurantName) : form.slug}
                    className="bg-gray-100 cursor-not-allowed"
                    placeholder={mode === 'create' ? 'Otomatik' : ''}
                  />
                </RestaurantFormField>
              </div>
              <div className="mt-4">
                <RestaurantFormField label="Açıklama">
                  <textarea
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                  />
                </RestaurantFormField>
              </div>

              <RestaurantFormSectionTitle>İletişim & konum</RestaurantFormSectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RestaurantFormField label="Telefon" error={fieldErrors.phone}>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    value={form.phone || ''}
                    onChange={(e) => {
                      setForm({ ...form, phone: e.target.value });
                      if (fieldErrors.phone) setFieldErrors({ ...fieldErrors, phone: '' });
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      const r = validateTurkishMobilePhone(value);
                      setFieldErrors((prev) => ({ ...prev, phone: r.valid ? '' : (r.message ?? '') }));
                    }}
                    className={fieldErrors.phone ? 'border-red-300' : ''}
                    placeholder="+905551234567"
                    maxLength={14}
                  />
                </RestaurantFormField>
                <RestaurantFormField label="Telefon 2" error={fieldErrors.phoneSecondary}>
                  <Input
                    type="tel"
                    inputMode="numeric"
                    value={form.phoneSecondary || ''}
                    onChange={(e) => {
                      setForm({ ...form, phoneSecondary: e.target.value });
                      if (fieldErrors.phoneSecondary) setFieldErrors({ ...fieldErrors, phoneSecondary: '' });
                    }}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      const r = validateTurkishMobilePhone(value);
                      setFieldErrors((prev) => ({ ...prev, phoneSecondary: r.valid ? '' : (r.message ?? '') }));
                    }}
                    className={fieldErrors.phoneSecondary ? 'border-red-300' : ''}
                    placeholder="Opsiyonel"
                    maxLength={14}
                  />
                </RestaurantFormField>
                <div className="sm:col-span-2">
                  <Select
                    label="Servis bölgesi"
                    required
                    options={getZoneOptions()}
                    value={form.serviceZoneId || undefined}
                    onChange={(value) => {
                      setForm({ ...form, serviceZoneId: value as string });
                      if (fieldErrors.serviceZoneId) setFieldErrors({ ...fieldErrors, serviceZoneId: '' });
                    }}
                    placeholder={isLoadingZones ? "Yükleniyor..." : 'Bölge seçin'}
                    disabled={isLoadingZones}
                    searchable
                    error={fieldErrors.serviceZoneId ? true : false}
                  />
                  {form.serviceZoneId && (
                    <p className="mt-1 text-xs text-gray-500">Seçili: {getZoneLabel(form.serviceZoneId)}</p>
                  )}
                  {fieldErrors.serviceZoneId && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.serviceZoneId}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <RestaurantFormField
                    label={`Adres ${form.isAcceptingOrders ? '(sipariş için zorunlu)' : ''}`}
                    hint="Adres arayıp listeden seçin; konum otomatik atanır."
                  >
                    <div className="relative" ref={addressSuggestionsRef}>
                      <input
                        type="text"
                        value={addressInputValue}
                        onChange={(e) => {
                          const v = e.target.value;
                          setAddressInputValue(v);
                          if (!v.trim()) setForm((prev) => ({ ...prev, address: '', latitude: null, longitude: null }));
                          scheduleAddressSearch(v);
                        }}
                        onBlur={() => setTimeout(() => setAddressSuggestions([]), 200)}
                        placeholder="En az 3 karakter yazın, listeden seçin"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                        autoComplete="off"
                        aria-autocomplete="list"
                        aria-expanded={addressSuggestions.length > 0}
                      />
                      {isSearchingAddress && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                          Aranıyor...
                        </span>
                      )}
                      {addressSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-52 overflow-y-auto">
                          {addressSuggestions.map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => handleSelectAddress(s)}
                              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-0 text-sm text-gray-700"
                            >
                              {s.fullAddress || s.displayName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </RestaurantFormField>
                  {form.latitude != null && form.longitude != null && (
                    <p className="mt-1 text-xs text-[#2A9D8F]">
                      Konum atandı: {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                    </p>
                  )}
                </div>
              </div>

              <RestaurantFormSectionTitle>Mutfak kategorileri</RestaurantFormSectionTitle>
              <CuisineMultiSelect
                label="Mutfak türleri"
                options={cuisineCategories}
                value={form.cuisineCategoryIds}
                onChange={(cuisineCategoryIds) =>
                  setForm({ ...form, cuisineCategoryIds })
                }
                hint="Birden fazla mutfak seçebilirsiniz. Restoran tüm seçili kategorilerde listelenir."
                emptyMessage="Mutfak kategorisi bulunamadı. Admin panelinden Mutfak Kategorileri sekmesinden ekleyin."
              />

              <RestaurantFormSectionTitle>Teslimat modeli</RestaurantFormSectionTitle>
              <div>
                <Select
                  label="Teslimat sağlayıcı"
                  value={form.deliveryProviderType}
                  onChange={(value) => setForm({ ...form, deliveryProviderType: value as string })}
                  options={[
                    { value: 'PLATFORM', label: 'Platform kuryesi' },
                    { value: 'OWN_FLEET', label: 'Kendi kuryesi' },
                    { value: 'HYBRID', label: 'Hibrit (önce kendi, sonra platform)' },
                  ]}
                  size="md"
                />
              </div>

              <RestaurantFormSectionTitle>Yasal & ödeme</RestaurantFormSectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RestaurantFormField label="Yasal unvan" error={fieldErrors.legalName}>
                  <Input
                    value={form.legalName || ''}
                    onChange={(e) => {
                      setForm({ ...form, legalName: e.target.value });
                      if (fieldErrors.legalName) setFieldErrors({ ...fieldErrors, legalName: '' });
                    }}
                    className={fieldErrors.legalName ? 'border-red-300' : ''}
                  />
                </RestaurantFormField>
                <RestaurantFormField label="Vergi no (VKN)" error={fieldErrors.taxNumber} hint="10 rakam">
                  <Input
                    value={form.taxNumber || ''}
                    onChange={(e) => {
                      setForm({ ...form, taxNumber: e.target.value.replace(/\D/g, '').slice(0, 10) });
                      if (fieldErrors.taxNumber) setFieldErrors({ ...fieldErrors, taxNumber: '' });
                    }}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      const r = validateVkn(v);
                      setFieldErrors((prev) => ({ ...prev, taxNumber: r.valid ? '' : r.message }));
                    }}
                    placeholder="4540536920"
                    maxLength={10}
                    className={fieldErrors.taxNumber ? 'border-red-300' : ''}
                  />
                </RestaurantFormField>
                <div className="sm:col-span-2">
                  <RestaurantFormField label="IBAN" required error={fieldErrors.iban} hint="TR + 24 rakam">
                    <Input
                      value={form.iban || ''}
                      onChange={(e) => {
                        setForm({ ...form, iban: e.target.value });
                        if (fieldErrors.iban) setFieldErrors({ ...fieldErrors, iban: '' });
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        const r = validateIban(v, true);
                        setFieldErrors((prev) => ({ ...prev, iban: r.valid ? '' : r.message }));
                      }}
                      placeholder="TR330006100519786457841326"
                      className={fieldErrors.iban ? 'border-red-300' : ''}
                    />
                  </RestaurantFormField>
                </div>
                <RestaurantFormField
                  label="Komisyon oranı"
                  hint="0–1 arası (örn: 0.10 veya 0,10 = %10)"
                  error={fieldErrors.commissionRate}
                >
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={commissionRateDisplay}
                    onChange={(e) => setCommissionRateDisplay(e.target.value)}
                    onBlur={() => {
                      const n = parseFloat(commissionRateDisplay.replace(',', '.'));
                      if (!Number.isNaN(n) && n >= 0 && n <= 1) {
                        setForm((prev) => ({ ...prev, commissionRate: n }));
                      }
                    }}
                    className={`w-full ${fieldErrors.commissionRate ? 'border-red-300' : ''}`}
                    placeholder="0.10"
                  />
                </RestaurantFormField>
                <div>
                  <RestaurantFormField label="Ödeme döngüsü">
                    <Select
                      value={form.billingCycle || ''}
                      onChange={(value) =>
                        setForm({
                          ...form,
                          billingCycle:
                            value === ''
                              ? undefined
                              : (value as 'AUTOMATIC' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM'),
                          customBillingCycleDays: value === 'CUSTOM' ? form.customBillingCycleDays : undefined,
                        })
                      }
                      options={[
                        { value: '', label: 'Seçiniz' },
                        { value: 'AUTOMATIC', label: 'Otomatik' },
                        { value: 'WEEKLY', label: 'Haftalık' },
                        { value: 'MONTHLY', label: 'Aylık' },
                        { value: 'CUSTOM', label: 'Özel (gün)' },
                      ]}
                      placeholder="Döngü seçin"
                      size="md"
                    />
                    {form.billingCycle === 'CUSTOM' && (
                      <div className="mt-2">
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={customBillingCycleDaysDisplay}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 3);
                            setCustomBillingCycleDaysDisplay(v);
                          }}
                          className={`w-full ${fieldErrors.customBillingCycleDays ? 'border-red-300' : ''}`}
                          placeholder="Gün (1–365)"
                        />
                        {fieldErrors.customBillingCycleDays && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrors.customBillingCycleDays}</p>
                        )}
                      </div>
                    )}
                  </RestaurantFormField>
                </div>
              </div>

              {mode === 'edit' && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Restoran Durumu
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, isAcceptingOrders: true })}
                      className={`flex-1 py-2 rounded-xl border text-sm font-semibold transition-all ${
                        form.isAcceptingOrders
                          ? 'bg-green-100 text-green-800 border-green-400 ring-2 ring-green-400 ring-offset-1 shadow-sm'
                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-green-300'
                      }`}
                    >
                      ● Çevrimiçi
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, isAcceptingOrders: false })}
                      className={`flex-1 py-2 rounded-xl border text-sm font-semibold transition-all ${
                        !form.isAcceptingOrders
                          ? 'bg-gray-100 text-gray-700 border-gray-400 ring-2 ring-gray-400 ring-offset-1 shadow-sm'
                          : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      ○ Çevrimdışı
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 flex gap-2 px-6 py-4 border-t border-gray-200 bg-[#F9FAFB] rounded-b-2xl">
            {mode === 'edit' && onDelete && restaurant?.isActive && (
              <Button type="button" variant="secondary" onClick={onDelete} className="text-red-600 border-red-200 hover:bg-red-50">
                Restoranı Sil
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting
                ? 'İşleniyor...'
                : mode === 'create'
                  ? 'Restoran oluştur'
                  : 'Kaydet'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              İptal
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

interface AdminProductFormModalProps {
  restaurant: RestaurantListItem;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

function AdminProductFormModal({ restaurant, onClose, onSuccess }: AdminProductFormModalProps) {
  const [categories, setCategories] = useState<AdminRestaurantCategory[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categoryId, setCategoryId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [displayOrder, setDisplayOrder] = useState('0');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingCategories(true);
    adminService
      .getRestaurantCategories(restaurant.id)
      .then((items) => {
        if (cancelled) return;
        setCategories(items);
        if (items.length > 0) {
          setCategoryId(items[0].id);
        }
      })
      .catch((error: any) => {
        if (cancelled) return;
        setErrorMessage(error.response?.data?.error?.message || 'Kategoriler yüklenemedi');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCategories(false);
      });
    return () => {
      cancelled = true;
    };
  }, [restaurant.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const trimmedName = name.trim();
    const parsedPrice = parseFloat(price.replace(',', '.'));
    if (!trimmedName || trimmedName.length < 2) {
      setErrorMessage('Ürün adı en az 2 karakter olmalıdır');
      return;
    }
    if (Number.isNaN(parsedPrice) || parsedPrice < 0.01) {
      setErrorMessage('Geçerli bir fiyat giriniz');
      return;
    }
    if (!categoryId) {
      setErrorMessage('Kategori seçiniz');
      return;
    }

    setIsSubmitting(true);
    try {
      await adminService.createRestaurantProduct(restaurant.id, categoryId, {
        name: trimmedName,
        description: description.trim() || undefined,
        price: parsedPrice,
        displayOrder: displayOrder.trim() ? parseInt(displayOrder, 10) : 0,
      });
      onSuccess(`"${trimmedName}" ürünü ${restaurant.name} restoranına eklendi.`);
    } catch (error: any) {
      setErrorMessage(error.response?.data?.error?.message || 'Ürün eklenemedi');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="max-w-lg w-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Ürün Ekle</h3>
            <p className="text-sm text-gray-500 mt-0.5">{restaurant.name}</p>
          </div>
          <Button variant="secondary" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        {errorMessage && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <Select
              label="Kategori"
              value={categoryId || undefined}
              onChange={(value) => setCategoryId(value as string)}
              options={categories.map((category) => ({
                value: category.id,
                label: category.name,
              }))}
              placeholder={isLoadingCategories ? 'Yükleniyor...' : categories.length === 0 ? 'Kategori yok' : 'Kategori seçin'}
              disabled={isLoadingCategories || categories.length === 0}
              searchable
            />
            {!isLoadingCategories && categories.length === 0 && (
              <p className="mt-2 text-xs text-amber-700">
                Bu restoranda aktif kategori bulunmuyor. Önce restoran panelinden kategori oluşturulmalıdır.
              </p>
            )}
          </div>

          <RestaurantFormField label="Ürün adı" required>
            <Input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full"
            />
          </RestaurantFormField>

          <RestaurantFormField label="Açıklama">
            <textarea
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </RestaurantFormField>

          <div className="grid grid-cols-2 gap-4">
            <RestaurantFormField label="Fiyat (₺)" required>
              <Input
                type="text"
                inputMode="decimal"
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="57.90"
                className="w-full"
              />
            </RestaurantFormField>
            <RestaurantFormField label="Sıra">
              <Input
                type="text"
                inputMode="numeric"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(e.target.value.replace(/\D/g, ''))}
                className="w-full"
              />
            </RestaurantFormField>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={isSubmitting || categories.length === 0} className="flex-1">
              {isSubmitting ? 'Ekleniyor...' : 'Ürün Ekle'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              İptal
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

